import type { FC } from 'react'
import { useCallback } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import type { SxProps, Theme } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { addDays } from 'date-fns'
import { ENVIRONMENT_OPTIONS, EXPIRATION_OPTIONS } from '../../constants'
import type { ApiKeyFormState } from '../../types'
import { useApiKeyForm } from '../../hooks'

interface ConfigurationStepProps {
  readonly initialValues: ApiKeyFormState
  readonly isGenerating: boolean
  readonly error: string | null
  readonly onGenerate: (values: ApiKeyFormState) => void
  readonly onBack: () => void
}

export const ConfigurationStep: FC<ConfigurationStepProps> = ({
  initialValues,
  isGenerating,
  error,
  onGenerate,
  onBack,
}) => {
  const {
    name,
    environment,
    expirationDays,
    customDate,
    touched,
    validation,
    isFormValid,
    handleNameChange,
    handleEnvironmentChange,
    handleExpirationChange,
    handleCustomDateChange,
  } = useApiKeyForm({
    name: initialValues.name,
    environment: initialValues.environment,
    expirationDays: initialValues.expirationDays,
    customExpirationDate: initialValues.customExpirationDate,
  })

  const handleSubmit = useCallback(() => {
    if (isFormValid) {
      onGenerate({ name, environment, expirationDays, customExpirationDate: customDate })
    }
  }, [isFormValid, name, environment, expirationDays, customDate, onGenerate])

  return (
    <Box sx={containerSx}>
      <Typography variant="h6" sx={titleSx}>
        Configure Your API Key
      </Typography>

      <TextField
        label="Key Name"
        value={name}
        onChange={handleNameChange}
        error={touched && !validation.isValid}
        helperText={
          touched ? validation.error : 'A descriptive name for your API key (e.g., Dev Laptop)'
        }
        fullWidth
        disabled={isGenerating}
        sx={fieldSx}
      />

      <FormControl fullWidth sx={fieldSx} disabled={isGenerating}>
        <InputLabel>Environment</InputLabel>
        <Select value={environment} label="Environment" onChange={handleEnvironmentChange}>
          {ENVIRONMENT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={fieldSx} disabled={isGenerating}>
        <InputLabel>Expiration</InputLabel>
        <Select
          value={expirationDays === null ? 'null' : String(expirationDays)}
          label="Expiration"
          onChange={handleExpirationChange}
        >
          {EXPIRATION_OPTIONS.map((option) => (
            <MenuItem
              key={String(option.value)}
              value={option.value === null ? 'null' : String(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {expirationDays === 'custom' && (
        <DatePicker
          label="Expiration Date"
          value={customDate}
          onChange={handleCustomDateChange}
          minDate={addDays(new Date(), 1)}
          disabled={isGenerating}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: fieldSx,
              helperText: 'Pick a date using the calendar icon',
            },
          }}
        />
      )}

      {expirationDays === null && (
        <Alert severity="warning" sx={alertSx}>
          API keys without an expiration date pose a security risk. Consider setting an expiration.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={alertSx}>
          {error}
        </Alert>
      )}

      <Box sx={actionsSx}>
        <Button onClick={onBack} disabled={isGenerating}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || isGenerating}
          startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isGenerating ? 'Generating...' : 'Generate Key'}
        </Button>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 3,
}

const fieldSx: SxProps<Theme> = {
  mb: 3,
}

const alertSx: SxProps<Theme> = {
  mb: 2,
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4,
}
